import { useMemo } from "react"

// Tiny single-line sparkline for the per-row error % chart.
// Hand-rolled SVG (no charting lib) — repeated dozens of times per page,
// keep DOM lean. Data is 0..1 per day, oldest-first; days with no calls
// render as gaps, not zeros.
type Props = {
  errorPctByDay: number[]   // 0..1 per day
  callsByDay: number[]      // 0+ per day, same length as errorPctByDay
  width?: number
  height?: number
  onClick?: () => void
  ariaLabel?: string
}

export function ErrorRateSparkline({
  errorPctByDay,
  callsByDay,
  width = 110,
  height = 28,
  onClick,
  ariaLabel = "Error rate, last 30 days",
}: Props) {
  const path = useMemo(() => {
    if (errorPctByDay.length === 0) return ""
    const n = errorPctByDay.length
    // Y range fixed 0..max(0.05, observed) so a flat-zero line still has scale,
    // and a typical 1–5% spike is visible without auto-rescaling per row.
    const observedMax = Math.max(0.05, ...errorPctByDay)
    const stepX = width / Math.max(1, n - 1)
    let cmd: string[] = []
    let started = false
    errorPctByDay.forEach((pct, i) => {
      const calls = callsByDay[i] ?? 0
      if (calls === 0) {
        // Break the line on no-call days so we don't draw fake zeros.
        started = false
        return
      }
      const x = i * stepX
      const y = height - (pct / observedMax) * height
      cmd.push(`${started ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`)
      started = true
    })
    return cmd.join(" ")
  }, [errorPctByDay, callsByDay, width, height])

  const totalCalls = callsByDay.reduce((a, b) => a + b, 0)
  const totalErrors = errorPctByDay.reduce((sum, p, i) => sum + p * (callsByDay[i] ?? 0), 0)
  const overallPct = totalCalls === 0 ? 0 : (totalErrors / totalCalls) * 100

  // Tone the line based on the worst rate observed in the window.
  const peak = Math.max(0, ...errorPctByDay)
  const stroke =
    peak >= 0.20 ? "var(--color-status-error, #ef4444)" :
    peak >= 0.05 ? "var(--color-status-warning, #f59e0b)" :
    "var(--color-status-success, #10b981)"

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.() }}
      title={`${overallPct.toFixed(1)}% errors over ${totalCalls} calls (last 30d) — click for details`}
      aria-label={ariaLabel}
      className="group flex items-center gap-2 rounded-control border border-transparent px-1 py-0.5 transition hover:border-border hover:bg-white/5 focus:outline-none focus:ring-1 focus:ring-brand-red/40"
    >
      <svg width={width} height={height} className="overflow-visible">
        {totalCalls === 0 ? (
          <text x={width / 2} y={height / 2 + 3} textAnchor="middle" className="fill-app-muted text-[10px]">no calls</text>
        ) : (
          <>
            <line x1={0} y1={height} x2={width} y2={height} stroke="currentColor" strokeWidth={0.5} className="text-app-muted opacity-30" />
            <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}
      </svg>
      <span className="min-w-[42px] text-right font-mono text-xs text-app-muted group-hover:text-app-text">
        {totalCalls === 0 ? "—" : `${overallPct.toFixed(1)}%`}
      </span>
    </button>
  )
}
